'use client';

import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';

const sharedComponents: Components = {
  p: ({ children }) => <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">{children}</p>,
  h1: ({ children }) => <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4 mt-6">{children}</h1>,
  h2: ({ children }) => <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-3 mt-5">{children}</h2>,
  h3: ({ children }) => <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 mt-4">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-4 text-slate-700 dark:text-slate-300 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-4 text-slate-700 dark:text-slate-300 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="ml-4">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900 dark:text-slate-100">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => <a href={href} className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  code: ({ children }) => <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-sm font-mono text-slate-900 dark:text-slate-100">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-slate-600 dark:text-slate-400 my-4">{children}</blockquote>,
};

export interface MarkdownViewProps {
  content: string;
  className?: string;
  /** Prose size: sm (compact), default, lg (cruise-style) */
  size?: 'sm' | 'default' | 'lg';
}

export default function MarkdownView({ content, className = '', size = 'default' }: MarkdownViewProps) {
  const proseSize = size === 'sm' ? 'prose-sm' : size === 'lg' ? 'prose-lg' : '';
  return (
    <div className={`prose dark:prose-invert max-w-none prose-slate dark:prose-invert ${proseSize} ${className}`.trim()}>
      <ReactMarkdown components={sharedComponents} remarkPlugins={[remarkBreaks]}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
